/**
 * Enrichissement « avec IA » d'une fiche : bouton + modale en trois temps
 * (choix du budget → progression → résultat auditable).
 *
 * Le résultat n'affiche jamais le raisonnement interne du modèle : un résumé
 * de décision, les champs écrits avec leur provenance, les contradictions,
 * les valeurs rejetées faute de preuve, et les champs en attente de revue
 * humaine (Accepter / Rejeter) — voir plan V3 §38-40.
 */
import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/crmApi";
import { toast } from "sonner";
import BaseModal from "@/crm/components/Modal";
import {
  Bot, CheckCircle2, XCircle, AlertTriangle, Loader2, ExternalLink, ShieldQuestion,
} from "lucide-react";

const BUDGETS = [
  ["rapide", "Recherche rapide", "6 appels sources, 1 passe IA"],
  ["standard", "Recherche standard", "15 appels sources, 2 passes IA"],
  ["approfondie", "Recherche approfondie", "30 appels sources, 3 passes IA"],
];

const STATUS_BADGE = {
  OK: "bg-emerald-50 text-emerald-700",
  PARTIAL: "bg-amber-50 text-amber-700",
  NEEDS_REVIEW: "bg-violet-50 text-violet-700",
  BUDGET_EXCEEDED: "bg-amber-50 text-amber-700",
  FAILED: "bg-rose-50 text-rose-700",
};

export default function AiEnrichPanel({ company, onDone, buttonClassName }) {
  const [openModal, setOpenModal] = useState(false);
  const [budget, setBudget] = useState("standard");
  const [job, setJob] = useState(null);
  const [run, setRun] = useState(null);
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);

  const launch = async () => {
    try {
      const { data } = await api.post("/ai-enrichment/jobs", { company_id: company.id, budget });
      const jobId = data.jobs[0].job_id;
      setJob({ id: jobId, status: "QUEUED", progress: {} });
      timer.current = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/ai-enrichment/jobs/${jobId}`);
          setJob(j);
          if (["DONE", "FAILED", "CANCELLED"].includes(j.status)) {
            clearInterval(timer.current);
            if (j.status === "FAILED") toast.error(apiError(j.error) || "Enrichissement IA en échec");
            if (j.run_id) {
              const { data: r } = await api.get(`/ai-enrichment/runs/${j.run_id}`);
              setRun(r);
            }
            onDone?.();
          }
        } catch { /* un raté de polling n'est pas une erreur du job */ }
      }, 2000);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Lancement impossible");
    }
  };

  const review = async (field, action) => {
    try {
      await api.post(`/ai-enrichment/runs/${run.id}/review`, { field, action });
      toast.success(action === "accept" ? `${field} accepté` : `${field} rejeté`);
      const { data: r } = await api.get(`/ai-enrichment/runs/${run.id}`);
      setRun(r);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Revue impossible");
    }
  };

  const close = () => {
    // Le job continue côté serveur : fermer la modale n'annule rien.
    setOpenModal(false);
    setJob(null); setRun(null);
    clearInterval(timer.current);
  };

  const running = job && !["DONE", "FAILED", "CANCELLED"].includes(job.status);
  const result = job?.result;

  return (
    <>
      <button data-testid="ai-enrich-btn" disabled={company.excluded}
        onClick={() => setOpenModal(true)} className={buttonClassName}
        title="Enrichir avec l'IA : recherche multi-sources, décisions prouvées">
        <Bot size={15} /> Enrichir avec l'IA
      </button>

      {openModal && (
        <BaseModal onClose={close} label="Enrichissement IA" size="xl" dismissible={!running}>
          <h3 className="font-heading font-semibold text-slate-800 mb-1">Enrichissement IA</h3>
          <p className="text-sm text-slate-500 mb-4">{company.nom}</p>

          {/* 1. Configuration */}
          {!job && (
            <div>
              <div className="space-y-2 mb-4">
                {BUDGETS.map(([key, label, detail]) => (
                  <label key={key} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50">
                    <input type="radio" checked={budget === key} onChange={() => setBudget(key)} />
                    <div>
                      <div className="text-sm text-slate-700 font-medium">{label}</div>
                      <div className="text-xs text-slate-400">{detail}</div>
                    </div>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-400 mb-4">
                L'IA recherche via les sources activées, compare les preuves et ne retient
                aucune donnée sans source. Les cas ambigus vous sont soumis pour revue.
              </p>
              <button data-testid="ai-enrich-launch" onClick={launch}
                className="w-full py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700">
                Lancer l'enrichissement IA
              </button>
            </div>
          )}

          {/* 2. Progression */}
          {running && (
            <div className="py-6 text-center" data-testid="ai-enrich-progress">
              <Loader2 size={28} className="mx-auto mb-3 animate-spin text-indigo-500" />
              <div className="text-sm text-slate-700">{job.progress?.current_step || "En attente"}</div>
              <div className="mt-3 space-y-1 text-xs text-slate-400">
                {(job.progress?.steps_done || []).map((s, i) => (
                  <div key={i} className="flex items-center justify-center gap-1.5">
                    <CheckCircle2 size={12} className="text-emerald-500" /> {s}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 3. Résultat */}
          {job && !running && (
            <div data-testid="ai-enrich-result">
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_BADGE[result?.status] || "bg-slate-100 text-slate-600"}`}>
                  {result?.status || job.status}
                </span>
                {run?.model && <span className="text-xs text-slate-400">modèle {run.model}</span>}
                <span className="text-xs text-slate-400">{result?.evidences ?? 0} preuves</span>
              </div>
              {result?.summary && <p className="text-sm text-slate-600 mb-4">{result.summary}</p>}

              {(result?.updates_deterministes?.length || result?.updates_ia?.length) ? (
                <div className="mb-4">
                  <div className="text-xs font-medium text-slate-500 mb-1.5">Champs mis à jour</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(result.updates_deterministes || []).map((f) => (
                      <span key={f} className="text-xs px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700">{f}</span>
                    ))}
                    {(result.updates_ia || []).map((f) => (
                      <span key={f} className="text-xs px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700">{f} · IA</span>
                    ))}
                  </div>
                </div>
              ) : (
                !result?.needs_review?.length && (
                  <p className="text-sm text-slate-400 mb-4">Aucun champ nouveau — la fiche était déjà à jour.</p>
                )
              )}

              {(run?.needs_review || []).length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-violet-700 mb-2">
                    <ShieldQuestion size={13} /> Décisions IA à vérifier
                  </div>
                  {run.needs_review.map((f) => (
                    <div key={f.field} className="flex items-center justify-between gap-2 p-2.5 rounded-xl border border-violet-100 bg-violet-50/40 mb-1.5">
                      <div className="min-w-0">
                        <div className="text-sm text-slate-700"><span className="font-medium">{f.field}</span> : {String(f.value)}</div>
                        <div className="text-xs text-slate-400 truncate">{f.reason}</div>
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        <button onClick={() => review(f.field, "accept")}
                          className="px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white text-xs hover:bg-emerald-700">Accepter</button>
                        <button onClick={() => review(f.field, "reject")}
                          className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50">Rejeter</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {(result?.rejets_sans_preuve || []).length > 0 && (
                <div className="mb-4 text-xs text-slate-500">
                  <div className="flex items-center gap-1.5 font-medium text-rose-600 mb-1">
                    <XCircle size={13} /> Propositions rejetées faute de preuve
                  </div>
                  {result.rejets_sans_preuve.map((r, i) => (
                    <div key={i}>{r.field} : {String(r.value)}</div>
                  ))}
                </div>
              )}

              {(result?.contradictions || []).length > 0 && (
                <div className="mb-4 text-xs text-slate-500">
                  <div className="flex items-center gap-1.5 font-medium text-amber-600 mb-1">
                    <AlertTriangle size={13} /> Contradictions conservées
                  </div>
                  {result.contradictions.map((ct, i) => (
                    <div key={i}>{ct.field} : {(ct.concurrents || ct.values || []).join(" / ") || ct.explanation}</div>
                  ))}
                </div>
              )}

              {(run?.decision?.fields || []).some((f) => f.decision === "ACCEPT") && (
                <div className="text-xs text-slate-400 mb-4">
                  {run.decision.fields.filter((f) => f.decision === "ACCEPT").map((f) => (
                    <div key={f.field} className="flex items-center gap-1">
                      <ExternalLink size={11} /> {f.field} — {f.reason} ({f.evidence_ids.length} preuve{f.evidence_ids.length > 1 ? "s" : ""})
                    </div>
                  ))}
                </div>
              )}

              <button onClick={close} className="w-full py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
                Fermer
              </button>
            </div>
          )}
        </BaseModal>
      )}
    </>
  );
}
